/**
 * Putting an aircraft on a stand, by hand, on the apron view.
 *
 * The rules are the stand register's own (`stands.ts`): four ways a stand can
 * be wrong, and a stated order of preference. This file adds only what a
 * person dragging an aircraft needs — how long a flight holds a stand, what is
 * on a stand at a given moment, and the change to the register that an
 * assignment, a move or a release makes.
 *
 * ── how long a flight holds a stand ──────────────────────────────────────
 *
 * An arrival and its departure are one visit: the stand is held from the
 * arrival's best-known time to the departure's. An arrival with no known
 * departure holds it for a standard turn; a departure with no arrival on the
 * record holds it for the hour before it leaves. Both standards are PROPOSED.
 * The assignment is recorded against the arrival when there is one, which is
 * how the sample plan and every other screen read it.
 *
 * Pure. `now` is always passed in.
 */
import { bestKnown, flightName, type Flight } from "./flights";
import {
  candidatesFor,
  conflictsFor,
  type Conflict,
  type StandAssignment,
  type StandBlock,
  type StandRegister,
} from "./stands";

/** Minutes an arrival holds a stand when no departure is known. PROPOSED. */
export const STANDARD_TURN = 80;
/** Minutes a departure with no arrival on the record holds its stand. PROPOSED. */
export const DEPARTURE_HOLD = 60;

const MIN = 60_000;
const iso = (ms: number) => new Date(ms).toISOString();

export interface Occupancy {
  /** the movement the assignment is recorded against */
  flightId: string;
  from: string;
  to: string;
  /** the visit's name for a card: "SV1478 → SV1479", or one flight */
  label: string;
}

/** The other half of a turnaround, where the record pairs them. */
export function pairOf(flight: Flight, flights: readonly Flight[]): Flight | undefined {
  if (!flight.turnaround) return undefined;
  return flights.find((f) => f.turnaround === flight.turnaround && f.id !== flight.id);
}

export function occupancyFor(flight: Flight, flights: readonly Flight[]): Occupancy | null {
  const pair = pairOf(flight, flights);
  const arr = flight.direction === "arrival" ? flight : pair?.direction === "arrival" ? pair : undefined;
  const dep = flight.direction === "departure" ? flight : pair?.direction === "departure" ? pair : undefined;
  const arrAt = arr ? bestKnown(arr).at : null;
  const depAt = dep ? bestKnown(dep).at : null;
  if (arr && arrAt) {
    const to = depAt && Date.parse(depAt) > Date.parse(arrAt) ? depAt : iso(Date.parse(arrAt) + STANDARD_TURN * MIN);
    return { flightId: arr.id, from: arrAt, to, label: dep ? `${flightName(arr)} → ${flightName(dep)}` : flightName(arr) };
  }
  if (dep && depAt) {
    return { flightId: dep.id, from: iso(Date.parse(depAt) - DEPARTURE_HOLD * MIN), to: depAt, label: flightName(dep) };
  }
  return null;
}

/** The assignment that already holds this visit, either half. */
export function assignmentOf(register: StandRegister, flight: Flight, flights: readonly Flight[]): StandAssignment | undefined {
  const ids = new Set([flight.id, pairOf(flight, flights)?.id].filter(Boolean));
  return register.assignments.find((a) => ids.has(a.flightId));
}

/* ── what is on a stand at a moment ───────────────────────────────────── */

export interface StandMoment {
  /** on the stand at that moment */
  now?: StandAssignment;
  /** the next one to come on */
  next?: StandAssignment;
  /** minutes from the moment until the next one comes on */
  nextIn?: number;
}

export function standAt(register: StandRegister, code: string, at: Date): StandMoment {
  const t = at.getTime();
  const mine = register.assignments
    .filter((a) => a.standCode === code)
    .sort((a, b) => a.from.localeCompare(b.from));
  const now = mine.find((a) => Date.parse(a.from) <= t && t < Date.parse(a.to));
  const next = mine.find((a) => Date.parse(a.from) > t);
  return { now, next, nextIn: next ? Math.round((Date.parse(next.from) - t) / MIN) : undefined };
}

/* ── changing the plan ────────────────────────────────────────────────── */

export interface Placement {
  flight: Flight;
  standCode: string;
  occupancy: Occupancy;
  conflicts: Conflict[];
}

/** Everything wrong with putting this visit on this stand, the visit's own assignment set aside. */
export function checkPlacement(
  register: StandRegister,
  flights: readonly Flight[],
  blocks: readonly StandBlock[],
  flight: Flight,
  standCode: string,
): Placement | null {
  const occ = occupancyFor(flight, flights);
  if (!occ) return null;
  const visitFlight = flights.find((f) => f.id === occ.flightId) ?? flight;
  const own = assignmentOf(register, flight, flights);
  const conflicts = conflictsFor({
    register,
    flights,
    blocks,
    standCode,
    flight: visitFlight,
    window: { from: occ.from, to: occ.to },
    ignoreAssignmentId: own?.id,
  });
  return { flight: visitFlight, standCode, occupancy: occ, conflicts };
}

let seq = 0;
const newId = () => `sa-m${Date.now().toString(36)}${(++seq).toString(36)}`;

/**
 * The register with this visit on this stand. A visit already on a stand is
 * MOVED — one assignment per visit — and a move keeps the times it had.
 */
export function assign(
  register: StandRegister,
  flights: readonly Flight[],
  flight: Flight,
  standCode: string,
  by: string,
): StandRegister {
  const occ = occupancyFor(flight, flights);
  if (!occ) return register;
  const own = assignmentOf(register, flight, flights);
  if (own) {
    return {
      ...register,
      assignments: register.assignments.map((a) => (a.id === own.id ? { ...a, standCode, by, note: `moved from ${own.standCode}` } : a)),
    };
  }
  const next: StandAssignment = {
    id: newId(),
    standCode,
    flightId: occ.flightId,
    from: occ.from,
    to: occ.to,
    actual: false,
    by,
  };
  return { ...register, assignments: [...register.assignments, next] };
}

export function release(register: StandRegister, assignmentId: string): StandRegister {
  return { ...register, assignments: register.assignments.filter((a) => a.id !== assignmentId) };
}

/* ── the smart part: where everything still waiting could go ──────────── */

export interface Proposal {
  flight: Flight;
  standCode: string;
  label: string;
  because: string;
}

/**
 * A stand for every visit with none, in time order, each one placed before
 * the next is considered — so two flights are never offered the same stand
 * for the same hour. Only clear stands are proposed; a visit with nowhere
 * clear is left out and said so by the caller.
 */
export function proposeAll(
  register: StandRegister,
  flights: readonly Flight[],
  blocks: readonly StandBlock[],
  waiting: readonly Flight[],
): { proposals: Proposal[]; nowhere: Flight[] } {
  let working = register;
  const proposals: Proposal[] = [];
  const nowhere: Flight[] = [];
  const seen = new Set<string>();
  for (const f of waiting) {
    const occ = occupancyFor(f, flights);
    if (!occ || seen.has(occ.flightId)) continue;
    seen.add(occ.flightId);
    const visit = flights.find((x) => x.id === occ.flightId) ?? f;
    const best = candidatesFor({ register: working, flights, blocks, flight: visit, window: { from: occ.from, to: occ.to } }).find(
      (c) => c.clear,
    );
    if (!best) {
      nowhere.push(visit);
      continue;
    }
    proposals.push({ flight: visit, standCode: best.stand.code, label: occ.label, because: best.because });
    working = assign(working, flights, visit, best.stand.code, "proposal");
  }
  return { proposals, nowhere };
}
